/**
 * Pure helper that merges audioDB-backed recordings into the in-memory list
 * surfaced on Quick Start / New Encounter.
 *
 * Invariants this fences against the iPad duplicate-row regression:
 *  - A recording present in both `existing` and `stored` (matching id) is
 *    NEVER returned again — the caller already has it.
 *  - Returned entries are always shaped for the `RecordingsList.addRecording`
 *    API (id, blob, duration, timestamp, name).
 *  - The function is pure: no side effects, no Date.now() calls.
 *
 * Re-using this from a future "list view that reads audioDB directly"
 * refactor should keep the contract identical.
 */

/**
 * Minimal shape of an entry already in the in-memory list. We only need
 * the id to dedupe — the caller has the rest.
 */
export interface InMemoryRecordingRef {
  id: string;
}

/**
 * Shape of an entry as audioDB returns it. Mirrors AudioRecording in
 * `audioDB.ts` but kept narrow so this helper has no Dexie coupling.
 */
export interface StoredRecording {
  blob: Blob;
  createdAt: number;
  duration: number;
  id: string;
}

/**
 * Shape the in-memory list expects on add. Matches the inline object
 * literal that QuickStartPage previously built.
 *
 * Fields are ``readonly`` so a caller can't mutate ``name`` after the
 * fact and desync the displayed label from the dedupe key.
 */
export interface RecordingToAdd {
  readonly blob: Blob;
  readonly duration: number;
  readonly id: string;
  readonly name: string;
  readonly timestamp: Date;
}

/**
 * Return the subset of ``stored`` that is NOT yet represented in
 * ``existing``, shaped for ``RecordingsList.addRecording``. Names are
 * generated as ``Recording N`` where N continues from the existing
 * count to keep the displayed numbering stable across stops.
 */
export function mergeStoredRecordings(
  existing: ReadonlyArray<InMemoryRecordingRef>,
  stored: ReadonlyArray<StoredRecording>,
): RecordingToAdd[] {
  if (stored.length === 0) return [];
  const existingIds = new Set(existing.map((r) => r.id));
  const additions: RecordingToAdd[] = [];
  let nextIndex = existing.length;
  for (const rec of stored) {
    if (existingIds.has(rec.id)) continue;
    nextIndex += 1;
    additions.push({
      id: rec.id,
      blob: rec.blob,
      duration: rec.duration,
      timestamp: new Date(rec.createdAt),
      name: `Recording ${nextIndex}`,
    });
  }
  return additions;
}
